// web/src/lib/auth.js
// Auth hook plus "view as" role switching.
//
// API path convention: api.js prepends VITE_API_URL (e.g. https://api.stallside.io)
// to whatever path we pass. The backend mounts routes at /auth, /horses, /dashboard
// (no /api prefix). So our paths here must NOT include /api.

import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

const VIEW_AS_STORAGE_KEY = 'stallside.viewAsRole';

const ROLE_LEVEL = {
  owner:            5,
  manager:          4,
  trainer:          3,
  service_provider: 2,
  groom:            2,
  client:           1,
  rider:            1,
};

export function useAuth() {
  const qc = useQueryClient();
  const [viewAsRole, setViewAsRoleState] = useState(() => {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(VIEW_AS_STORAGE_KEY);
  });

  const { data: meResponse, isLoading } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get('/auth/me'),
    retry: false,
    staleTime: 60_000,
  });

  const user = meResponse?.user ?? meResponse ?? null;
  const barns = meResponse?.barns ?? [];

  useEffect(() => {
    if (!user || !viewAsRole) return;
    if (!canViewAs(user.role, viewAsRole)) {
      setViewAsRole(null);
    }
  }, [user, viewAsRole]);

  function setViewAsRole(role) {
    setViewAsRoleState(role);
    if (typeof window !== 'undefined') {
      if (role) window.localStorage.setItem(VIEW_AS_STORAGE_KEY, role);
      else window.localStorage.removeItem(VIEW_AS_STORAGE_KEY);
    }
  }

  const activeRole = viewAsRole || user?.role || null;
  const isViewingAs = Boolean(viewAsRole && user && viewAsRole !== user.role);

  return {
    user,
    barns,
    loading: isLoading,
    isLoading,
    activeRole,
    viewAsRole,
    isViewingAs,
    setViewAsRole,
    availableViewAsRoles: user ? viewAsRolesFor(user.role) : [],
    refetch: () => qc.invalidateQueries({ queryKey: ['me'] }),
  };
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (creds) => api.post('/auth/login', creds),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

export function useSignup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => api.post('/auth/signup', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/auth/logout'),
    onSuccess: () => {
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(VIEW_AS_STORAGE_KEY);
      }
      qc.clear();
    },
  });
}

// First-run barn setup. On success we invalidate ['me'] so the now-active barn
// (with the user's new owner role) flows through useAuth and the dashboard
// renders in place — no manual navigation needed.
export function useOnboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => api.post('/auth/onboard', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

function canViewAs(realRole, targetRole) {
  const real = ROLE_LEVEL[realRole] ?? 0;
  const target = ROLE_LEVEL[targetRole] ?? 0;
  return target <= real;
}

function viewAsRolesFor(realRole) {
  const real = ROLE_LEVEL[realRole] ?? 0;
  if (real < 4) return [];
  return Object.entries(ROLE_LEVEL)
    .filter(([role, level]) => level <= real && role !== realRole)
    .sort((a, b) => b[1] - a[1])
    .map(([role]) => role);
}
